import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CircleDollarSign, Plus } from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { formatBRL, formatDate } from "@/lib/utils";
import { financeService } from "@/services/finance.service";
import {
    CATEGORY_LABELS,
    FINANCE_STATUS_LABELS,
    FinanceStatusBadge,
} from "@/features/finance/components/FinanceStatusBadge";
import { CreateAccountDialog } from "@/features/finance/dialogs/CreateAccountDialog";

/**
 * Shared list page for Accounts Receivable and Accounts Payable.
 * Prop:
 *   - type: 'receivable' | 'payable'
 */
export default function AccountsListPage({ type = "receivable" }) {
    const navigate = useNavigate();
    const qc = useQueryClient();

    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [category, setCategory] = useState("");
    const [createOpen, setCreateOpen] = useState(false);

    const isReceivable = type === "receivable";
    const title = isReceivable ? "Contas a Receber" : "Contas a Pagar";
    const description = isReceivable
        ? "Gerencie recebíveis de propostas aprovadas e lançamentos manuais."
        : "Gerencie pagamentos a fornecedores, tributos, salários e demais despesas.";

    const list = useQuery({
        queryKey: ["finance-accounts", { type, page, search, status, category }],
        queryFn: () =>
            financeService.listAccounts({
                type,
                page,
                pageSize: 20,
                search,
                status: status || undefined,
                category: category || undefined,
                sortBy: "created_at",
                sortOrder: "desc",
            }),
    });

    const createMut = useMutation({
        mutationFn: (payload) => financeService.createAccount(payload),
        onSuccess: (doc) => {
            toast.success(`Conta ${doc.number} criada.`);
            qc.invalidateQueries({ queryKey: ["finance-accounts"] });
            qc.invalidateQueries({ queryKey: ["finance-dashboard"] });
            setCreateOpen(false);
            navigate(`/finance/${isReceivable ? "receivables" : "payables"}/${doc.id}`);
        },
        onError: (e) =>
            toast.error(e?.response?.data?.detail || e?.message || "Falha ao criar conta."),
    });

    const columns = [
        {
            key: "number",
            header: "Nº",
            cell: (r) => (
                <div className="flex items-center gap-2">
                    <span className="grid h-8 w-8 place-items-center rounded-md bg-muted text-muted-foreground">
                        <CircleDollarSign className="h-4 w-4" />
                    </span>
                    <div className="font-mono text-sm font-medium">{r.number}</div>
                </div>
            ),
        },
        {
            key: "description",
            header: "Descrição",
            cell: (r) => (
                <div>
                    <div className="text-sm">{r.description}</div>
                    {r.proposal_number ? (
                        <div className="text-xs text-muted-foreground">
                            Proposta {r.proposal_number}
                        </div>
                    ) : (
                        r.work_order_number && (
                            <div className="text-xs text-muted-foreground">
                                OS {r.work_order_number}
                            </div>
                        )
                    )}
                </div>
            ),
        },
        {
            key: "counterparty",
            header: isReceivable ? "Cliente" : "Fornecedor",
            cell: (r) => (
                <div className="text-sm">{r.counterparty_name || "—"}</div>
            ),
        },
        {
            key: "category",
            header: "Categoria",
            cell: (r) => (
                <span className="text-xs text-muted-foreground">
                    {CATEGORY_LABELS[r.category] || r.category}
                </span>
            ),
        },
        {
            key: "installments_summary",
            header: "Parcelas",
            cell: (r) => {
                const total = (r.installments || []).length;
                const paid = (r.installments || []).filter((i) => i.status === "paga").length;
                return (
                    <span className="text-xs text-muted-foreground">
                        {paid}/{total}
                    </span>
                );
            },
        },
        {
            key: "total_amount",
            header: "Total",
            cell: (r) => <div className="text-right font-medium">{formatBRL(r.total_amount)}</div>,
        },
        {
            key: "balance",
            header: "Saldo",
            cell: (r) => (
                <div className="text-right">
                    {r.status === "paga"
                        ? <span className="text-xs text-emerald-600">Quitada</span>
                        : formatBRL(r.balance)}
                </div>
            ),
        },
        {
            key: "status",
            header: "Status",
            cell: (r) => <FinanceStatusBadge status={r.status} />,
        },
    ];

    return (
        <div className="space-y-4" data-testid={`finance-${type}-list`}>
            <PageHeader
                title={title}
                description={description}
                actions={
                    <Button
                        onClick={() => setCreateOpen(true)}
                        data-testid="finance-create-account-button"
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Criar conta
                    </Button>
                }
            />

            <Card>
                <DataTable
                    columns={columns}
                    query={list.data}
                    loading={list.isLoading}
                    onSearch={(v) => { setSearch(v); setPage(1); }}
                    onPageChange={(p) => setPage(p)}
                    onRowClick={(r) =>
                        navigate(`/finance/${isReceivable ? "receivables" : "payables"}/${r.id}`)
                    }
                    dataTestId={`finance-${type}-datatable`}
                    emptyState={{
                        title: isReceivable ? "Nenhuma conta a receber" : "Nenhuma conta a pagar",
                        description: isReceivable
                            ? "Aprove uma proposta comercial ou clique em 'Criar conta' para começar."
                            : "Clique em 'Criar conta' para adicionar despesas manuais.",
                    }}
                    toolbarLeft={
                        <div className="flex flex-wrap gap-2">
                            <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
                                <SelectTrigger className="h-9 w-[160px]" data-testid={`finance-${type}-status-filter`}>
                                    <SelectValue placeholder="Todos status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos status</SelectItem>
                                    {Object.entries(FINANCE_STATUS_LABELS).map(([v, l]) => (
                                        <SelectItem key={v} value={v}>{l}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select value={category || "all"} onValueChange={(v) => { setCategory(v === "all" ? "" : v); setPage(1); }}>
                                <SelectTrigger className="h-9 w-[160px]" data-testid={`finance-${type}-category-filter`}>
                                    <SelectValue placeholder="Todas categorias" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todas categorias</SelectItem>
                                    {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                                        <SelectItem key={v} value={v}>{l}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    }
                />
            </Card>

            <CreateAccountDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                defaultType={type}
                onSubmit={(payload) => createMut.mutateAsync(payload)}
                submitting={createMut.isPending}
            />
        </div>
    );
}
